import React from 'react';
import { Helmet } from 'react-helmet'; // can be a hasMeta() HOC in the future
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { decodeHTML, encodeHTML, encodeSpecialCharacters, decodeSpecialCharacters, encodeTitle, isValid } from '../../utils';

/*
Helmet component can either encode all special characters or none. We've set it up to encode none and then use either the encodeSpecialCharacters (from the Helmet source code) or the encodeHTML functions to handle encoding on a field-by-field basis.

Most tag values can be passed to encodeSpecialCharacters() but any fields containing HTML, such as meta descriptions containing anchor tags, should be passed to encodeHTML(). We may not have to encode fields where we aren't expecting special characters (ex. og:url).
*/

const MetaTags = ({
  title,
  description,
  url,
  tags,
  category,
  articleType,
  ogType,
  ogTitle,
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogDescription,
  facebookTout,
  twitterImg,
  twitterDescription,
  keywords,
  canonicalUrl,
  noIndex,
  isFeaturedContent,
  trackerHead,
  pubDate,
  modifiedDate,
  videoUrl,
  imagePreviewSize,
  rssCategory,
  affilimate = false,
  isAffiliate,
  isRecipe,
}) => {

const rssSrc = rssCategory && `https://www.purewow.com/rss/feed_national${rssCategory !== 'homepage' ? '_' + rssCategory : ''}.xml`;
const parsedTracker = (isValid(trackerHead)) ? parse(trackerHead) : null;
const scriptTrackers = parsedTracker ? parsedTracker.filter(tracker => tracker.type == 'script') : [];
const trackerArray = scriptTrackers.map((tracker, i) => {
  if (tracker.props.dangerouslySetInnerHTML) {
    return <script>{tracker.props.dangerouslySetInnerHTML.__html}</script>;
  }
  return tracker;
})
const imagePreviewValue = imagePreviewSize ? `max-image-preview:${imagePreviewSize}` : null;
const metaArticleType = (isValid(articleType)) ? articleType.toLowerCase() : null;
return (
  <Helmet encodeSpecialCharacters={true}>
    {title && <title>{decodeSpecialCharacters(title)}</title>}
    {description && <meta name="description" content={decodeSpecialCharacters(description)} />}
    {url && <meta property="og:url" content={url} />}
    {ogType && <meta property="og:type" content={ogType} />}
    {ogTitle && <meta property="og:title" content={decodeSpecialCharacters(ogTitle)} />}
    {ogImage && <meta property="og:image" content={ogImage} />}
    {ogImageWidth && <meta property="og:image:width" content={ogImageWidth} />}
    {ogImageHeight && <meta property="og:image:height" content={ogImageHeight} />}
    {ogDescription && <meta property="og:description" content={decodeSpecialCharacters(ogDescription)} />}
    {modifiedDate && <meta property="og:updated_time" content={modifiedDate} />}
    {facebookTout && <meta name="FacebookShareMessage" content={facebookTout}/>}
    <meta name="twitter:card" content="summary_large_image" />
    {ogTitle && <meta name="twitter:title" content={decodeSpecialCharacters(ogTitle)} />}
    {twitterImg && <meta name="twitter:image:src" content={twitterImg} />}
    <meta name="twitter:site" content="@PureWow" />
    {url && <meta name="twitter:url" content={url} />}
    {isFeaturedContent && <meta name="robots" content="noindex"/>}
    {twitterDescription && <meta name="twitter:description" content={decodeSpecialCharacters(twitterDescription)} />}
    {keywords && <meta name="keywords" content={keywords} />}
    {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    {noIndex && <meta name="robots" content="noindex" />}
    {pubDate && <meta name="article:published_time" content={pubDate} />}
    {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
    { trackerArray.length > 0 && trackerArray }
    {videoUrl && <meta property="og:video" content={videoUrl} />}
    {imagePreviewValue && <meta name="robots" content={imagePreviewValue} />}
    {rssCategory && <link rel="alternate" type="application/rss+xml" href={rssSrc} />}
    {affilimate && <meta name="am-api-token" content="XXOcb2hmBumn" />}
    {isAffiliate && <meta name="am-tags:category" content={category} />}
    {isAffiliate && <meta name="am-tags:tags" content={tags} />}
    {metaArticleType && <meta property="pw:article_type" content={metaArticleType} />}
  </Helmet>
)};

MetaTags.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  url: PropTypes.string,
  ogType: PropTypes.string,
  ogTitle: PropTypes.string,
  ogImage: PropTypes.string,
  ogImageWidth: PropTypes.string,
  ogImageHeight: PropTypes.string,
  ogDescription: PropTypes.string,
  facebookTout: PropTypes.string,
  twitterImg: PropTypes.string,
  twitterDescription: PropTypes.string,
  keywords: PropTypes.string,
  canonicalUrl: PropTypes.string,
  advertiser: PropTypes.string,
};

export default MetaTags;
